import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch, isAdmin } from './utils/api';
import { color, naira, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip } from './ui/kit';

/**
 * The tax bands and contribution rates payroll is calculated from.
 *
 * Payroll refuses to run until somebody has confirmed these against current
 * law. That gate was already enforced on the server, but there was no screen
 * anywhere that showed the rates, said why payroll was blocked, or let anybody
 * confirm them -- so payroll could not be run at all except by calling the API
 * by hand. A mandatory check with no door is not a control, it is a wall.
 *
 * Editing anything here un-confirms the configuration, deliberately: whoever
 * signed off the old figures did not review the new ones.
 */

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 13,
};

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail || j.message || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const pct = (n) => `${Number(n).toFixed(Number(n) % 1 ? 2 : 0)}%`;

export default function PayrollRates({ notify, onConfirmed }) {
  const [configs, setConfigs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [bands, setBands] = useState([]);
  const [signer, setSigner] = useState('');
  const [source, setSource] = useState('');
  const admin = isAdmin();

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await req('/api/payroll/rate-configs');
      setConfigs(Array.isArray(d) ? d : (d.configs || []));
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <Banner tone="danger" title="Could not load the rates">{error}</Banner>;
  if (!configs) return null;
  if (!configs.length) {
    return (
      <Banner tone="danger" title="No payroll rate configuration exists">
        Payroll cannot be calculated without tax bands. One has to be created
        before any run.
      </Banner>
    );
  }

  const cfg = configs[0];
  const startEdit = () => {
    setBands((cfg.tax_bands || []).map((b) => ({
      sequence: b.sequence,
      lower_bound: String(b.lower_bound ?? ''),
      upper_bound: b.upper_bound == null ? '' : String(b.upper_bound),
      rate_percent: String(b.rate_percent ?? ''),
    })));
    setEditing(true);
  };

  const saveBands = async () => {
    const payload = bands.map((b, i) => ({
      sequence: i + 1,
      lower_bound: Number(b.lower_bound || 0),
      upper_bound: b.upper_bound === '' ? null : Number(b.upper_bound),
      rate_percent: Number(b.rate_percent || 0),
    }));
    setBusy(true);
    try {
      await req(`/api/payroll/rate-configs/${cfg.id}/bands`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      notify?.('Tax bands replaced. They now need confirming again.', 'success');
      setEditing(false);
      await load();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (signer.trim().length < 3) {
      notify?.('Enter the name of the person taking responsibility', 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await req(`/api/payroll/rate-configs/${cfg.id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmed_by: signer.trim(),
          source_reference: source.trim() || null,
        }),
      });
      notify?.(r.message || 'Rates confirmed.', 'success');
      setSigner(''); setSource('');
      await load();
      onConfirmed?.();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: space(1.5) }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15 }}>Tax bands and contribution rates</h3>
          <div style={{ fontSize: 12.5, color: color.textSecondary }}>
            {cfg.name} &middot; effective from {cfg.effective_from}
          </div>
        </div>
        {cfg.is_confirmed
          ? <Chip tone="success">Confirmed</Chip>
          : <Chip tone="danger">Not confirmed</Chip>}
      </div>

      {!cfg.is_confirmed && (
        <Banner tone="danger" title="Payroll cannot run until these are confirmed">
          {cfg.warning || 'These rates have not been verified against current tax law.'}
          {' '}Every payroll run is refused until somebody reviews the figures
          below and signs for them. That is why creating a run is failing.
        </Banner>
      )}

      {cfg.is_confirmed && (
        <Banner tone="info" title="Signed off">
          Confirmed by {cfg.confirmed_by}
          {cfg.confirmed_at ? ` on ${new Date(cfg.confirmed_at).toLocaleDateString('en-GB')}` : ''}
          {cfg.source_reference ? ` against ${cfg.source_reference}` : ''}.
          Editing any figure withdraws this.
        </Banner>
      )}

      <h4 style={{ margin: `${space(2)} 0 ${space(1)}`, fontSize: 13.5 }}>
        Annual tax bands
      </h4>
      {!editing ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: color.textSecondary, fontSize: 12 }}>
              <th style={{ padding: '6px 4px' }}>From</th>
              <th style={{ padding: '6px 4px' }}>To</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Rate</th>
            </tr>
          </thead>
          <tbody>
            {(cfg.tax_bands || []).map((b) => (
              <tr key={b.sequence} style={{ borderTop: `1px solid ${color.border}` }}>
                <td style={{ padding: '6px 4px' }}>{naira(b.lower_bound)}</td>
                <td style={{ padding: '6px 4px' }}>
                  {b.upper_bound == null
                    ? <span style={{ color: color.textSecondary }}>and above</span>
                    : naira(b.upper_bound)}
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600 }}>
                  {pct(b.rate_percent)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <>
          {bands.map((b, i) => (
            <div key={i} style={{ display: 'grid', gap: 8, marginBottom: 6,
                                  gridTemplateColumns: '1fr 1fr 90px 36px' }}>
              <input style={inputStyle} inputMode="decimal" value={b.lower_bound}
                     placeholder="From"
                     onChange={(e) => {
                       const n = [...bands]; n[i] = { ...b, lower_bound: e.target.value };
                       setBands(n);
                     }} />
              <input style={inputStyle} inputMode="decimal" value={b.upper_bound}
                     placeholder="To (blank = and above)"
                     onChange={(e) => {
                       const n = [...bands]; n[i] = { ...b, upper_bound: e.target.value };
                       setBands(n);
                     }} />
              <input style={inputStyle} inputMode="decimal" value={b.rate_percent}
                     placeholder="%"
                     onChange={(e) => {
                       const n = [...bands]; n[i] = { ...b, rate_percent: e.target.value };
                       setBands(n);
                     }} />
              <Btn size="sm" variant="secondary"
                   onClick={() => setBands(bands.filter((_, j) => j !== i))}>&times;</Btn>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: space(1) }}>
            <Btn size="sm" variant="secondary"
                 onClick={() => setBands([...bands, {
                   sequence: bands.length + 1, lower_bound: '',
                   upper_bound: '', rate_percent: '' }])}>
              Add band
            </Btn>
            <Btn size="sm" onClick={saveBands} disabled={busy}>
              {busy ? 'Saving…' : 'Replace bands'}
            </Btn>
            <Btn size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Btn>
          </div>
          <div style={{ fontSize: 11.5, color: color.textMuted, marginTop: 6 }}>
            Bands must meet exactly &mdash; each one starts where the last ended,
            with no gap and no overlap, or some income is taxed twice or not at
            all. The last band is left open-ended.
          </div>
        </>
      )}

      <h4 style={{ margin: `${space(2)} 0 ${space(1)}`, fontSize: 13.5 }}>
        Contributions and allowances
      </h4>
      <div style={{ display: 'grid', gap: 8,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        {(cfg.rate_items || []).map((r) => (
          <div key={r.code} style={{ background: color.bg,
                                     borderRadius: radius.sm, padding: '8px 10px' }}>
            <div style={{ fontSize: 10.5, color: color.textSecondary,
                          letterSpacing: 0.3 }}>
              {r.code.replace(/_/g, ' ')}
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>
              {r.basis === 'FIXED' ? Number(r.value).toLocaleString() : pct(r.value)}
              <span style={{ fontWeight: 400, fontSize: 11.5,
                             color: color.textSecondary }}> of {r.basis.toLowerCase()}</span>
            </div>
          </div>
        ))}
      </div>

      {admin && (
        <div style={{ borderTop: `1px solid ${color.border}`,
                      marginTop: space(2), paddingTop: space(2) }}>
          {!editing && (
            <Btn size="sm" variant="secondary" onClick={startEdit}>
              Edit tax bands
            </Btn>
          )}
          {!cfg.is_confirmed && (
            <div style={{ marginTop: space(1.5) }}>
              <div style={{ fontSize: 12.5, color: color.textSecondary,
                            marginBottom: 8 }}>
                Confirming is an assertion by a named person that these figures
                match current law. It is recorded permanently and payroll is
                calculated from them.
              </div>
              <div style={{ display: 'grid', gap: 8, marginBottom: 8,
                            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
                <input style={inputStyle} value={signer}
                       placeholder="Name of the person signing off"
                       onChange={(e) => setSigner(e.target.value)} />
                <input style={inputStyle} value={source}
                       placeholder="Checked against (e.g. Nigeria Tax Act 2025)"
                       onChange={(e) => setSource(e.target.value)} />
              </div>
              <Btn size="sm" onClick={confirm} disabled={busy}>
                {busy ? 'Confirming…' : 'Confirm these rates'}
              </Btn>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
